import React from "react";
import AnimationRevealPage from "helpers/AnimationRevealPage.js";
import { useParams } from "react-router-dom";
import { Container } from "components/misc/Layouts";
import tw from "twin.macro";
import Header from "components/headers/light.js";
import Footer from "components/footers/FiveColumnWithInputForm.js";

import conferencesList from "../conferences.json";
import styled from "styled-components";
import { Helmet } from "react-helmet";
import { PrimaryButton as PrimaryButtonBase } from "../components/misc/Buttons";

const ContentWithPaddingLg = tw.div`max-w-screen-xl mx-auto py-20 lg:py-24`;
const HeadingRow = tw.div`flex`;
const Heading = tw.h2`text-2xl font-black tracking-wide text-gray-900`;
const Subheading = tw.h3`font-bold text-primary-500 mt-12 text-xl`;

const Description = styled.div`
  ${tw`mt-6 text-left text-sm md:text-base lg:text-lg font-medium leading-relaxed text-secondary-100`}
  p {
    font-size: 1rem;
  }
  li {
    font-size: 1rem;
  }
  h2 {
    font-size: 1.25rem;
  }
  ol,
  ul {
    padding-top: 10px;
  }
  li span {
    font-size: 1.125rem;
  }
`;

const Button = styled(PrimaryButtonBase)`
  ${tw`rounded-full mt-8 uppercase tracking-wider py-4 text-sm hover:shadow-xl transform hocus:translate-x-px hocus:-translate-y-px focus:shadow-outline`}
`;

const ImageGrid = tw.div`mt-10 grid grid-cols-1 md:grid-cols-2 justify-center gap-8`;

const GridImage = styled.img`
  ${tw`w-full h-full object-contain rounded-lg shadow-lg`}
`;

export default () => {
  const { slug } = useParams();

  const post = conferencesList.find((a) => a.slug === slug);

  if (!post) {
    return (
      <HeadingRow>
        <Heading>404 Conference not found</Heading>
      </HeadingRow>
    );
  }

  return (
    <>
      <Helmet>
        <title>{post.title} | Conferences | MAI Motion</title>
        <meta name="description" content={`${post.shortDescription} - ${post.title} presented by MSK Doctors at leading medical conferences.`} />
        <meta name="keywords" content={`${post.title.toLowerCase()}, ${post.slug.includes('mai-motion') ? 'MAI Motion, AI motion analysis, biomechanics, markerless motion capture' : post.slug.includes('onmri') ? 'onMRI, AI MRI analysis, medical imaging, musculoskeletal MRI' : post.slug.includes('orthobiologic') ? 'orthobiologic injections, knee osteoarthritis, movement quality, PRP therapy' : post.slug.includes('craft') ? 'CRAFT framework, sit-to-stand, squat analysis, biomechanical assessment' : 'knee surgery, robotics, motion biology'}, MSK Doctors, clinical research, medical conferences, ${post.shortDescription.includes('Porto') ? 'European Symposium, Porto Portugal' : post.shortDescription.includes('BOA') ? 'British Orthopaedic Association, BOA Congress' : post.shortDescription.includes('Casablanca') ? 'United European Academy, Casablanca' : 'Sorbonne University, Paris'}, musculoskeletal health, healthcare innovation`} />
        <link rel="canonical" href={`https://maimotion.com/conferences/${slug}`} />
        <meta property="og:title" content={`${post.title} | Conferences | MAI Motion`} />
        <meta property="og:description" content={`${post.shortDescription} - ${post.title} presented by MSK Doctors at leading medical conferences.`} />
        <meta property="og:type" content="article" />
        <meta property="og:url" content={`https://maimotion.com/conferences/${slug}`} />
        <meta property="og:image" content={post.image} />
        <meta name="twitter:title" content={`${post.title} | Conferences | MAI Motion`} />
        <meta name="twitter:description" content={`${post.shortDescription} - ${post.title} presented by MSK Doctors.`} />
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:image" content={post.image} />
        <meta name="robots" content="index,follow" />
        <meta name="author" content="MSK Doctors Research Team" />
        <meta name="article:published_time" content={post.date} />
        <meta name="article:section" content="Medical Conferences" />
        <meta name="article:tag" content="Musculoskeletal Health" />
        <script type="application/ld+json">
          {JSON.stringify({
            "@context": "https://schema.org",
            "@type": "Article",
            "headline": post.title,
            "description": post.shortDescription,
            "image": post.image,
            "author": {
              "@type": "Organization",
              "name": "MSK Doctors"
            },
            "publisher": {
              "@type": "Organization",
              "name": "MAI Motion",
              "logo": {
                "@type": "ImageObject",
                "url": "https://maimotion.com/logo.png"
              }
            },
            "datePublished": post.date,
            "dateModified": post.date,
            "mainEntityOfPage": {
              "@type": "WebPage",
              "@id": `https://maimotion.com/conferences/${slug}`
            }
          })}
        </script>
      </Helmet>
      <AnimationRevealPage>
        <Header />
        <Container>
          <ContentWithPaddingLg>
            <HeadingRow>
              <Heading>{post.title}</Heading>
            </HeadingRow>
            <Description>
              <div dangerouslySetInnerHTML={{ __html: post.authors }} />
            </Description>
            {/* Image Grid */}
            {post.images && post.images.length > 0 && (
              <ImageGrid>
                {post.images.map((src, index) => (
                  <GridImage
                    key={index}
                    src={
                      "https://d3ekn1do43nbxb.cloudfront.net/conferences/" + src
                    }
                    alt={`Conference Image ${index + 1}`}
                  />
                ))}
              </ImageGrid>
            )}
            {/* <Subheading>Highlight</Subheading> */}
            <Description>
              <div dangerouslySetInnerHTML={{ __html: post.html }} />
            </Description>
          </ContentWithPaddingLg>
        </Container>
        <Footer />
      </AnimationRevealPage>
    </>
  );
};
